import { Copy } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Select } from '@/components/ui/Select'
import type { SchoolClass } from '@/features/academic/types'
import { useCopyTimetable } from '../api'
import { sectionLabel } from '@/features/academic/sectionLabel'

interface CopyFromDialogProps {
  sectionId: number
  sectionName: string
  classes: SchoolClass[]
  onOpenChange: (open: boolean) => void
}

/** Fills the empty cells of this grid with another section's subjects (no teachers or rooms). */
export function CopyFromDialog({ sectionId, sectionName, classes, onOpenChange }: CopyFromDialogProps) {
  const { t } = useTranslation('timetable')
  const copy = useCopyTimetable(sectionId)
  const [source, setSource] = useState('')
  const options = classes.flatMap((c) =>
    c.sections
      .filter((s) => s.id !== sectionId)
      .map((s) => ({ value: s.id, label: sectionLabel(c.name, s.name, ' – ') })),
  )

  const onCopy = async () => {
    try {
      const res = await copy.mutateAsync(Number(source))
      toast.success(
        t('{{count}} lessons copied', { count: res.copied }),
        res.skipped
          ? t('{{count}} skipped (cell taken or not in the curriculum)', { count: res.skipped })
          : undefined,
      )
      onOpenChange(false)
    } catch (e) {
      toast.error(t('Could not copy the timetable'), errorMessage(e))
    }
  }

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="sm"
      icon={<Copy />}
      title={t('Copy another timetable')}
      description={t(
        'Empty cells of {{section}} get the subjects of the chosen section. Teachers and rooms are not copied.',
        {
          section: sectionName,
        },
      )}
      dismissible={!copy.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={copy.isPending}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => void onCopy()} loading={copy.isPending} disabled={!source}>
            {t('Copy lessons')}
          </Button>
        </>
      }
    >
      <FormField label={t('Copy from')} required>
        <Select
          options={options}
          placeholder={t('Choose a section')}
          value={source}
          onChange={(e) => setSource(e.target.value)}
        />
      </FormField>
    </Dialog>
  )
}
